import type { Enum } from './enum.types';

export const AUTH_FLOW_TYPE = {
  LOGIN: 'Login',
  REGISTRATION: 'Registration',
} as const;
export type AUTH_FLOW_TYPE = Enum<typeof AUTH_FLOW_TYPE>;

export const UserEvents = {
  LOGIN: 'login',
  REGISTER: 'register',
  LOGOUT: 'logout',
  USERCHANGE: 'user-change',
  ERROR: 'error',
  READY: 'ready',
  REQUESTCLOSE: 'request-close',
  FAV_CLUB_UPDATED: 'profile-fav-club-updated',
  FAV_TEAM_UPDATED: 'profile-fav-national-team-updated',
  UEFA_DIALOG_READY: 'uefa-dialog-ready',
  UEFA_DIALOG_CLOSE: 'uefa-dialog-close',
  FOLLOWED_CLUB_UPDATED: 'user-schema-update',
} as const;
export type UserEvents = Enum<typeof UserEvents>;

export const LoginSources = {
  HEADER: 'Header',
  CONTENT: 'Content Section',
  MENU: 'Menu',
} as const;
export type LoginSources = Enum<typeof LoginSources>;

export type IdpUser = {
  UID?: string;
  originalUser?: any;
  created?: string;
  profile?: {
    nickname?: string;
    name?: string;
    firstName?: string;
    email?: string;
    thumbnailURL?: string;
    lastName?: string;
    age?: number;
    birthDay?: number;
    birthMonth?: number;
    birthYear?: number;
    country?: string;
    locale?: string;
  };
  data?: {
    subscriptions?: {
      subscribeToAll?: boolean;
    };
    ticketing?: {
      ticketsDeliveryEmail?: string;
    };
    favourites: {
      club?: string;
      clubsFollowed?: string;
      competitions?: {
        [comp: string]: {
          club?: string;
        };
      };
      nationalTeamsFollowed?: string;
      nationalTeam?: string;
      womenNationalTeam?: string;
    };
    accounts: {
      acquisition: {
        regRedirectURL: 'https://www.uefa.com/uefachampionsleague/clubs/50051--real-madrid/';
        regSource: 'UCL Web';
        regDate: '2022-05-16T15:03:35.238Z';
        regCampaign: 'https://www.uefa.com/uefachampionsleague/clubs/50051--real-madrid/';
      };
    };
  };
  subscriptions?: {
    newsletters?: {
      [newsletterId: string]: {
        email?: {
          isSubscribed: boolean;
          lastUpdatedSubscriptionState?: string;
        };
      };
    };
    sponsors?: {
      all?: {
        email?: {
          isSubscribed: boolean;
          lastUpdatedSubscriptionState?: string;
        };
      };
    };
  };
};

export type FavouriteClubUpdateEvent = {
  competition?: 'ucl' | 'uel' | 'men' | 'women';
  label?: string;
  teamId?: string;
  value?: string;
  englishLabel?: string;
};

export interface IdpEventEmitter {
  on(event: UserEvents, callback: (e?: any) => void): IdpEventEmitter;
  once(event: UserEvents, callback: (e?: any) => void): IdpEventEmitter;
  emit(event: UserEvents, ...args: unknown[]): IdpEventEmitter;
}

export type IdpResponse = {
  status: string;
  errorCode: number;
  errorMessage?: string;
};

export type LoginRegisterOptions = { containerID?: string; redirectUrl?: string };
export type ProfileOptions = { modal?: boolean; selector?: string; screen?: string };
export type LiteRegistrationOptions = {
  selector: string;
  newsletterId?: string;
  campaign?: string;
  redirectUrl?: string;
};

export interface IdpManager {
  init(config: { apiKey: string; locale: string }): void;
  onReady(): Promise<IdpUser>;
  isLogged(): boolean;
  getCurrentUser(): Promise<IdpUser>;
  getJwtToken(fields?: string | string[]): Promise<string>;
  logout(): Promise<void>;
  getEventEmitter(): IdpEventEmitter;
  showLogin(options: LoginRegisterOptions): IdpEventEmitter;
  showSocialRegister(options: LoginRegisterOptions): IdpEventEmitter;
  showRegister(options: LoginRegisterOptions): IdpEventEmitter;
  showProfile(options: ProfileOptions): IdpEventEmitter;
  showLiteRegistration(options: LiteRegistrationOptions): IdpEventEmitter;
  /** @deprecated NOT GDPR COMPLIANT! */
  showMinimalLiteRegistration(options: LiteRegistrationOptions): IdpEventEmitter;
  showScreen(options: unknown): IdpEventEmitter;
  getFavouriteTeamCode(): Promise<string>;
  setFavouriteTeam(team: string): Promise<IdpResponse>;
  removeFavouriteTeam(): Promise<IdpResponse>;
  getFavouriteWomenTeamCode(): Promise<string>;
  setFavouriteWomenTeam(team: string): Promise<IdpResponse>;
  removeFavouriteWomenTeam(): Promise<IdpResponse>;
  getFavouriteClubId(comp?: string): Promise<string | { [comp: string]: { club: string } }>;
  setFavouriteClub(comp: string, club?: string): Promise<IdpResponse>;
  getFollowedClubIds(): Promise<string[]>;
  getFollowedTeamIds(): Promise<string[]>;
  addFollowedClub(clubIds: string): Promise<string[]>;
  removeFollowedClub(clubIds: string): Promise<string[]>;
  addFollowedTeam(teamCodes: string): Promise<string[]>;
  removeFollowedTeam(teamCodes: string): Promise<string[]>;
}

declare global {
  interface Window {
    IdpManager?: IdpManager;
    showUefaLogin: (source?: LoginSources) => Promise<IdpEventEmitter>;
    showUefaRegister: (source?: LoginSources) => Promise<IdpEventEmitter>;
    useCustomAuthDialog?: boolean;
  }
}
